/** external */
import _ from "lodash";
import moment from "moment";

/** internal */
import { getUserInfo } from "Login/modules/LoginModule";
import { getChannelInstance } from "modules/PusherModule";
import {
  getDateTimeDifference,
  getIncrementedDate,
  getTodaysDate,
} from "Utils/DateTimeHelpers";
import { getBasicDeviceInfo } from "Utils";
import {
  isMobileOrTablet,
  isSafeExamBrowser,
  isTabletByUserAgent,
} from "Utils/DeviceDetectHelpers";
import {
  getIsTestEnded,
  isWorksheetWebView,
  generateQuestionsHierarchy,
  isVisualWorksheetElement,
} from "Worksheet/modules/WorksheetUtils";
import { QUESTION_UID_MULTI_PART } from "Worksheet/modules/WorksheetConstants";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import {
  getStudentWorksheetReopenState,
  getStudentExtraTime,
  getStudentWorksheetPauseState,
  getStudentWorksheetDataFromEvents,
} from "Worksheet/components/WorksheetTimer/WorksheetTimerUtils";

// * Relative
import { getStudentAssignmentItemFromCache } from "./WorksheetMonitoringGraphqlHelpers";
import { MONITORING_EVENT_TYPE } from "./WorksheetMonitoringConstants";

export const MONITORING_TABS = {
  MONITORING: "MONITORING",
  PROGRESS: "PROGRESS",
};

export { getIsTestEnded };

/**
 *
 * @param {*} events
 * @param {*} eventType!
 * @param {*} afterEventTime!
 * @returns Filter and ordered Events
 */
const withOrder = (_events, eventTypes = [], afterEventTime = null) => {
  let events = _events;

  // * Filter by event types
  if (!_.isEmpty(eventTypes)) {
    events = _.filter(events, e => _.includes(eventTypes, e.eventType));
  }

  // * Filter by event date
  if (afterEventTime) {
    events = _.filter(
      events,
      ({ eventTime }) =>
        eventTime && moment(eventTime).isSameOrAfter(moment(afterEventTime))
    );
  }

  return _.orderBy(events, ["eventTime"], ["asc"]);
};

/**
 *
 * @param {*} data
 * @returns Nested question structure with question numbers
 */
const simplifyQuestionTree = data => {
  let globalQuestionCounter = 0; // Global counter for main questions

  const mapNode = (node, parentInfo = { depth: 0, number: null }) => {
    const id = _.get(node, "item.id");
    const questionType = _.get(node, "item.questionType.uid");
    const label = _.get(node, "item.questionTitleV2.value", "");
    const isSection = questionType === "section";
    let questionNumber = null;

    // For non-section questions at top level or section children
    if (!isSection && parentInfo.depth <= 1) {
      globalQuestionCounter += 1;
      questionNumber = String(globalQuestionCounter);
    }
    // For children of multi-part questions (sub-questions)
    else if (!isSection && parentInfo.depth > 1 && parentInfo.number) {
      const childIndex = parentInfo.childIndex || 1;
      questionNumber = `${parentInfo.number}.${childIndex}`;
    }

    // Create the node object
    const result = {
      id,
      questionType,
      ...(isSection && { label }),
      ...(questionNumber && { questionNumber }),
    };

    // Process children if they exist
    if (_.size(node.children) > 0) {
      if (isSection) {
        // For section's children, pass current depth info
        result.children = _.map(node.children, child =>
          mapNode(child, {
            depth: parentInfo.depth + 1,
            number: null,
          })
        );
      } else {
        // For question's children (multi-part sub-questions), use parent.child numbering
        result.children = _.map(node.children, (child, idx) =>
          mapNode(child, {
            depth: parentInfo.depth + 1,
            number: questionNumber,
            childIndex: idx + 1,
          })
        );
      }
    }

    return result;
  };

  return _.map(data.data, node => mapNode(node));
};
/**
 *
 * @param {*} tree
 * @returns Flattened question structure with question numbers
 */
const simplifyQuestionFlat = data => {
  // Helper function to recursively collect questions
  const collectQuestions = nodes => {
    return _.flatMap(nodes, node => {
      // For sections, only process their children
      if (node.questionType === "section") {
        return node.children ? collectQuestions(node.children) : [];
      }

      // For multi-part questions and other question types, include the node itself
      const result = [_.pick(node, ["id", "questionType", "questionNumber"])];

      // And also include their children if any
      if (node.children && node.children.length > 0) {
        result.push(...collectQuestions(node.children));
      }

      return result;
    });
  };

  // Collect and sort questions
  return _.sortBy(
    collectQuestions(data),
    node =>
      node.questionNumber?.replace(/\d+/g, n => n.padStart(3, "0")) || "999"
  );
};

/**
 *
 * @param {*} data
 * @returns { flatFormat : [], treeFormat: [] }
 */
const getFormattedQuestionForMonitoring = data => {
  const _edges = _.get(data, "content.questions.edges", []);
  const edges = _.filter(
    _edges,
    question =>
      !isVisualWorksheetElement(_.get(question, "questionType.uid", ""))
  );
  const questionHierarchy = generateQuestionsHierarchy(edges);
  const treeFormat = simplifyQuestionTree(questionHierarchy);
  const flatFormat = simplifyQuestionFlat(treeFormat);

  return { flatFormat, treeFormat };
};

/**
 *
 * @param {*} student
 * @returns Student personal information
 */
const getStudentsInformation = data => {
  const students = _.map(_.get(data, "assignedStudents", []), student => {
    return {
      id: _.get(student, "student.id", null),
      name: getFormattedUserName({
        user: _.get(student, "student", {}),
      }),
      profileImage: _.get(student, "student.profileImage", null),
      studentAssignmentId: _.get(student, "id"),
      submissionId: _.get(student, "submission.id", null),
      isLockScreenEnabled: {
        defaultValue: _.get(data, "isLockScreenEnabled", false),
        value: _.get(student, "submission.isLockScreenEnabled", null),
      },
      isOnScreenCalculatorEnabled: {
        defaultValue: _.get(data, "isOnScreenCalculatorEnabled", false),
        value: _.get(student, "submission.isOnScreenCalculatorEnabled", null),
      },
      isSafeBrowserEnabled: {
        defaultValue: _.get(data, "isSafeBrowserEnabled", false),
      },
    };
  });
  return { students, totalStudents: _.size(students) };
};

/**
 *
 * @param {*} student
 * @returns Student submission details
 */
const getStudentSubmissionDetails = (student, worksheetQuestions) => {
  const submissions = _.get(student, "submission.questionSubmissions", {});
  const questionsToChildMap = {};
  _.forEach(_.get(worksheetQuestions, "treeFormat", []), section => {
    _.forEach(_.get(section, "children", []), question => {
      const questionId = _.get(question, "id", null);
      if (_.get(question, "questionType") === QUESTION_UID_MULTI_PART) {
        questionsToChildMap[questionId] = _.map(
          _.get(question, "children", []),
          "id"
        );
      }
    });
  });

  const questions = {};
  _.forEach(_.get(submissions, "edges", []), ({ node }) => {
    const question = _.get(node, "question", {});
    const questionId = _.get(question, "id", null);
    if (questionId) {
      questions[questionId] = {
        isAttempted: _.get(node, "isQuestionAttempted", false),
        childQuestions: _.get(questionsToChildMap, questionId, []) || [],
      };
    }
  });

  return {
    questions,
    totalAttempted: _.get(submissions, "totalQuestionAttempted", 0),
  };
};

/**
 *
 * @param {*} data
 * @returns Student Level Mapping for question attempts and online status
 */
const getStudentQuestionAttempts = (data, questions) => {
  const onlineStudents = {};
  const questionAttempts = {};

  const students = _.get(data, "assignedStudents", []);
  const studentIds = _.map(students, student => student?.student?.id);
  _.forEach(studentIds, studentId => {
    if (!studentId) return;
    const student = _.find(students, s => s?.student?.id === studentId);
    if (student) {
      questionAttempts[studentId] = getStudentSubmissionDetails(
        student,
        questions
      );
      onlineStudents[studentId] = false;
    }
  });
  return { questionAttempts, onlineStudents };
};

/**
 *
 * @param {*} data
 * @returns Student Screen Absences Mapping
 */
const getStudentScreenAbsences = data => {
  const screenAbsences = {};
  let isTimedTest = false;

  _.forEach(_.get(data, "assignedStudents", []), student => {
    const studentId = _.get(student, "student.id", null);
    const assignment = _.get(student, "assignment", {});
    const isTimedTestEnabled = _.get(assignment, "isTimedTestEnabled", false);
    isTimedTest = isTimedTestEnabled;
    if (!student) return;

    // * Get all absence events for the student
    const events = _.get(student, "events", []);
    const resumeEvents = withOrder(events, [MONITORING_EVENT_TYPE.RESUME]);
    const absenceEvents = _.filter(resumeEvents, e => e?.duration);
    const duration = _.sumBy(absenceEvents, e =>
      _.parseInt(_.get(e, "duration") || 0, 10)
    );
    screenAbsences[studentId] = { instances: _.size(absenceEvents), duration };
  });

  return { screenAbsences, isTimedTest };
};

/**
 *
 * @param {*} data
 * @returns Get assignment dates to display
 */
export const getAssignmentDates = data => {
  const assignment = _.get(data, "assignedStudents.0.assignment", {});
  const isFixedStartTime = _.get(assignment, "isFixedStartTime", false);
  const deadline = _.get(data, "deadline", null);
  const closeDate = _.get(assignment, "closeSubmissionDate", null);
  const publishedAt = _.get(assignment, "publishedAt", null);
  const duesOn = deadline && !isFixedStartTime ? moment(deadline) : null;
  const closesOn = closeDate && !isFixedStartTime ? moment(closeDate) : null;
  const opensOn = publishedAt && isFixedStartTime ? moment(publishedAt) : null;
  return {
    dates: {
      duesOn,
      opensOn,
      closesOn,
      isFixedStartTime,
      publishedAt: moment(publishedAt),
    },
  };
};

/**
 *
 * @param {*} data
 * @returns Formatted data for monitoring dashboard and student progress
 */
export const getWorksheetMonitoringFormattedData = data => {
  const isSafeBrowserEnabled = _.get(data, "isSafeBrowserEnabled", false);
  const isLockScreenEnabled = _.get(data, "isLockScreenEnabled", false);
  const showAbsenceEvents = isSafeBrowserEnabled || isLockScreenEnabled;

  const questions = getFormattedQuestionForMonitoring(data);

  const monitoringData = {
    questions,
    showAbsenceEvents,
    isSafeBrowserEnabled,
    ...getAssignmentDates(data),
    ...getStudentsInformation(data),
    ...getStudentScreenAbsences(data),
    ...getStudentQuestionAttempts(data, questions),
  };

  return monitoringData;
};

/**
 *
 * @returns List of online students
 */
export const updateOnlineStudents = ({ channel, setOnlineStudents }) => {
  channel?.presence?.get((err, members) => {
    if (err) return;
    const onlineStudents = {};

    // * Getting online students from presence members
    _.forEach(members, member => {
      const userId = _.get(member, "clientId", null);
      if (userId) onlineStudents[userId] = true;
    });

    setOnlineStudents(prev => {
      const next = {};
      new Set([..._.keys(prev || {}), ..._.keys(onlineStudents)]).forEach(
        id => (next[id] = !!onlineStudents[id])
      );
      if (_.isEqual(next, prev)) return prev;
      return next;
    });
  });
};

/**
 *
 * @param {*} assessmentId
 * @param {*} eventType
 * @param {*} eventData
 * Dispatch event to Ably channel for Worksheet Monitoring
 */
// Ably connection states where publishing is not possible
const ABLY_UNPUBLISHABLE_STATES = new Set([
  "disconnected",
  "suspended",
  "closed",
  "failed",
]);

export const sendWorksheetMonitoringEvent = (
  assignmentId,
  eventType,
  eventData = {}
) => {
  const userInfo = getUserInfo();
  const studentId = _.get(userInfo, "id");
  const organizationId = _.get(userInfo, "org_id");
  const worksheetAblyChannel = getChannelInstance({
    channelType: "worksheet",
    data: { organizationId, assessmentId: assignmentId },
  });
  if (!worksheetAblyChannel) return;

  const connectionState = worksheetAblyChannel?.connectionManager?.state?.state;
  if (!connectionState || ABLY_UNPUBLISHABLE_STATES.has(connectionState)) {
    return;
  }

  worksheetAblyChannel.publish("WORKSHEET_MONITORING", {
    metadata: JSON.stringify({
      eventType,
      studentId,
      assignmentId,
      ...eventData,
    }),
  });
};

/**
 * Retrieves device information and augments it with Safe Exam Browser details if applicable.
 * @returns {Object} The device information, including updated browser info if in Safe Exam Browser.
 */
export const getDeviceInfo = t => {
  const isMobile = isMobileOrTablet();
  const isTablet = isTabletByUserAgent();
  const isWebView = isWorksheetWebView();
  const isInSafeExamBrowser = isSafeExamBrowser();
  let deviceInfo = getBasicDeviceInfo() || {};
  if (!_.isObject(deviceInfo)) deviceInfo = {};

  // * Determine device type
  const deviceType = isTablet ? "tablet" : isMobile ? "mobile" : "desktop";
  deviceInfo = { ...deviceInfo, isWebView, deviceType };

  // * For Safe Exam Browser
  if (isInSafeExamBrowser) {
    const sebBrowserInfo = _.get(window, "SafeExamBrowser.version", "");
    const sebVersion = _.get(_.split(sebBrowserInfo, "_"), 2, "");
    deviceInfo = {
      ...deviceInfo,
      browser: {
        name: "Safe Exam Browser",
        version: sebVersion,
      },
    };
  }

  // * For WebView (Student App)
  if (isWebView) {
    deviceInfo = {
      ...deviceInfo,
      browser: {
        name: _.isFunction(t) ? t("common:student_app") : "Student App",
      },
    };
  }

  return deviceInfo;
};

/**
 *
 * @param {*} deviceInfo
 * @returns Display for device information
 */
const getDisplayDeviceInformation = deviceInfo => {
  if (!deviceInfo || _.isEmpty(deviceInfo)) return null;

  const osName = _.get(deviceInfo, "os.name", "Unknown OS");
  const osVersion = _.get(deviceInfo, "os.version", "");
  const osInfo = osVersion ? `${osName} ${osVersion}` : osName;

  // Format browser information
  const browserName = _.get(deviceInfo, "browser.name");
  const browserVersion = _.get(deviceInfo, "browser.version", "");

  // Adjust browser name if needed (e.g., "Chrome" to "Google Chrome")
  const formattedBrowserName = _.includes(browserName, "Chrome")
    ? "Google Chrome"
    : browserName;

  return {
    type: _.get(deviceInfo, "deviceType", "desktop"),
    device: `${osInfo}`,
    browser: browserName
      ? `${formattedBrowserName}${browserVersion ? ` v${browserVersion}` : ""}`
      : null,
  };
};

/**
 *
 * @param {*} assignment
 * @param {*} events
 * @returns Time left
 */
export const getStudentTimeLeft = (_events, data) => {
  const events = withOrder(_events);

  const {
    endTime,
    startTime,
    extraTime,
    isReopened,
    reopenDuration,
    currentPauseDuration,
    totalPauseDuration,
  } = getStudentWorksheetDataFromEvents(events, data);

  const {
    publishedAt = null,
    isTimedTestEnabled = false,
    closeSubmissionDate: _closeSubmissionDate = null,
    timedTestDuration: _timedTestDuration = "0",
    isFixedStartTime: _isFixedStartTime = false,
  } = _.get(data, "assignment", {});

  const now = getTodaysDate();
  const closeSubmissionDate =
    _closeSubmissionDate || moment().add(100, "years").toDate(); // Far future date for no close date
  const timedTestDuration = _.toNumber(_timedTestDuration || "0") * 60; // Convert minutes to seconds
  const isFixedStartTime = isReopened ? false : _isFixedStartTime; // If reopened, treat as flexible start time
  const isTestStarted = Boolean(startTime);
  const isTestEnded = Boolean(endTime);

  // Compute total paused duration
  const computedPauseDuration = totalPauseDuration + currentPauseDuration;

  // Case 1:  Non- timed test or test ended
  if (!isTimedTestEnabled || isTestEnded) return 0;

  // Case 2: Reopened test
  if (isReopened) return reopenDuration;

  // Case 3: Test not started
  if (!isTestStarted) {
    // Case 3.1: Flexible start time
    if (!isFixedStartTime) {
      const adjustedCloseDate = getIncrementedDate({
        key: "seconds",
        date: closeSubmissionDate,
        incrementBy: computedPauseDuration,
      });

      const closingWindow = getDateTimeDifference({
        date2: now,
        differenceBy: "seconds",
        date1: adjustedCloseDate,
      });

      return Math.min(timedTestDuration, closingWindow) + extraTime;
    }

    // Case 3.2: Fixed start time
    // - If scheduled start time is still in the future
    if (moment(publishedAt).isAfter(moment(now))) {
      return timedTestDuration + extraTime;
    }

    // - Test start time has passed but student hasn't started yet
    const adjustedCloseDate = getIncrementedDate({
      key: "seconds",
      date: closeSubmissionDate,
      incrementBy: computedPauseDuration + extraTime,
    });

    return getDateTimeDifference({
      date2: now,
      differenceBy: "seconds",
      date1: adjustedCloseDate,
    });
  }

  // Case 4: Test started
  const testEndTime = getIncrementedDate({
    key: "seconds",
    date: startTime,
    incrementBy: timedTestDuration + computedPauseDuration,
  });
  const testDurationCap = getDateTimeDifference({
    date2: now,
    date1: testEndTime,
    differenceBy: "seconds",
  });

  const adjustedCloseDate = getIncrementedDate({
    key: "seconds",
    date: closeSubmissionDate,
    incrementBy: computedPauseDuration,
  });
  const closeDateCap = getDateTimeDifference({
    date2: now,
    differenceBy: "seconds",
    date1: adjustedCloseDate,
  });

  return Math.min(testDurationCap, closeDateCap) + extraTime;
};

/**
 *
 * @param {*} data
 * @returns Student Device Information
 */
export const getStudentDeviceInfo = _events => {
  let events = withOrder(_events, [
    MONITORING_EVENT_TYPE.RESUME,
    MONITORING_EVENT_TYPE.START,
  ]);
  events = events.filter(e => e?.metadata?.deviceInfo);
  const deviceInfo = _.get(_.last(events), "metadata.deviceInfo");
  return getDisplayDeviceInformation(deviceInfo);
};

/**
 *
 * @param {*} _events
 * @returns Student absence events
 */
export const getStudentAbsenceEvent = _events => {
  const events = withOrder(_events, [MONITORING_EVENT_TYPE.RESUME]);
  const absenceEvents = _.filter(events, e => e?.duration);
  const instances = _.size(absenceEvents);
  const sumDuration = e => _.parseInt(_.get(e, "duration") || 0, 10);
  const duration = _.sumBy(absenceEvents, sumDuration);
  return { instances, duration };
};

/**
 * @param {*} { submissionStatus }
 * @returns String indicating if the test is ended based on submission status
 */
const getStudentSubmissionStatus = (data, events) => {
  const submissionStatus = _.get(data, "submission.statusV2", null);
  switch (submissionStatus) {
    case "TURNED_IN_ON_TIME":
    case "TURNED_IN_LATE":
    case "SUBMISSION_NOT_REQUIRED":
    case "SUBMITTED":
      return "SUBMITTED";
    case "RESUBMISSION_NOT_SUBMITTED":
    case "NOT_SUBMITTED":
      return "NOT_SUBMITTED";
    case "DRAFT":
    case "SUBMISSION_PENDING":
    case "REQUEST_RESUBMISSION":
    case "REQUEST_RESUBMISSION_OVERDUE":
    case "OVERDUE":
    case "SCHEDULED": {
      const isTimedTest = _.get(data, "assignment.isTimedTestEnabled", false);
      const hasStartEvent = _.some(events, { eventType: "START" });
      if (getStudentTimeLeft(events, data) <= 0 && isTimedTest) {
        return hasStartEvent ? "SUBMITTED" : "NOT_SUBMITTED";
      }
      return hasStartEvent ? "IN_PROGRESS" : "NOT_STARTED";
    }
    default:
      return "NOT_STARTED";
  }
};

/**
 *
 * @param {*} studentAssignmentId
 * @param {*} key
 * @returns Get Student Item based on key
 */
export const getStudentItem = (studentAssignmentId, key) => {
  const data = getStudentAssignmentItemFromCache(studentAssignmentId);
  const allEvents = withOrder(_.get(data, "events", []));
  const { reopenEventTime } = getStudentWorksheetReopenState(allEvents, data);
  let events = allEvents;
  if (reopenEventTime) {
    events = withOrder(_.get(data, "events", []), [], reopenEventTime);
  }

  const keys = _.isArray(key) ? key : [key];
  const response = _.map(keys, key => {
    switch (key) {
      case "EXTRA_TIME": {
        return getStudentExtraTime(events);
      }
      case "TIME_LEFT": {
        return getStudentTimeLeft(events, data);
      }
      case "DEVICE_INFO": {
        return getStudentDeviceInfo(allEvents);
      }
      case "ABSENCE_EVENT": {
        return getStudentAbsenceEvent(allEvents);
      }
      case "PAUSE_STATE": {
        return getStudentWorksheetPauseState(allEvents, data);
      }
      case "REOPEN_STATE": {
        return getStudentWorksheetReopenState(events, data);
      }
      case "SUBMISSION_STATUS": {
        return getStudentSubmissionStatus(data, events);
      }
      case "STARTED_AT": {
        const startEvent = _.find(allEvents, { eventType: "START" });
        if (!startEvent) return "-";
        return moment(startEvent?.eventTime).format("DD MMM YYYY, hh:mm A");
      }
      case "SUBMITTED_AT": {
        const submittedAt = _.get(data, "submission.submittedAt");
        if (!submittedAt) return null;
        return moment(submittedAt).format("DD MMM YYYY, hh:mm A");
      }
      default:
        return data;
    }
  });
  return _.isArray(key) ? response : _.first(response);
};

/**
 *
 * @param {*} students
 * @returns { isLockScreenEnabled, isOnScreenCalculatorEnabled }
 */
export const getStudentLockBrowserAndCalculatorStatus = students => {
  const getEffectiveValue = (student, key) => {
    const value = _.get(student, `${key}.value`, null);
    const defaultValue = _.get(student, `${key}.defaultValue`, false);
    return _.isNull(value) ? defaultValue : value;
  };

  const lockScreenStatuses = _.map(students, student =>
    getEffectiveValue(student, "isLockScreenEnabled")
  );

  const calculatorStatuses = _.map(students, student =>
    getEffectiveValue(student, "isOnScreenCalculatorEnabled")
  );

  const isLockScreenEnabled =
    _.size(students) === 1
      ? _.first(lockScreenStatuses)
      : _.every(lockScreenStatuses, Boolean);

  const isOnScreenCalculatorEnabled =
    _.size(students) === 1
      ? _.first(calculatorStatuses)
      : _.every(calculatorStatuses, Boolean);

  const isSafeBrowserEnabled = _.get(
    students,
    "[0].isSafeBrowserEnabled.defaultValue",
    false
  );

  return {
    isLockScreenEnabled,
    isSafeBrowserEnabled,
    isOnScreenCalculatorEnabled,
  };
};
